import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { get } from 'lodash'
import { FormattedMessage } from 'react-intl'
import Dropzone from 'react-dropzone'
import { MaterialLoader } from 'components/loaders'
import { FieldError } from 'components/misc'
import { getToken } from 'utils/token'
import { SUPPORTED_FILE_TYPES, SUPPORTED_IMG_TYPES } from 'constants/enums'
import styles from './image-picker-field.styl'

export default class ImagePickerField extends Component {
  static propTypes = {
    storeRemoved: PropTypes.bool,
    notification: PropTypes.func,
    isFile: PropTypes.bool,
    onPutBlob: PropTypes.func,
    input: PropTypes.object,
    meta: PropTypes.object,
    user: PropTypes.object
  }

  static defaultProps = {
    storeRemoved: false,
    isFile: true
  }

  state = {
    isLoading: false
  }

  componentDidMount() {
    const { input } = this.props

    this.initializeBlob(input.value)
  }

  onDrop = (accepted, rejected, input) => {
    const { isFile } = this.props
    const supportedTypes = isFile ? SUPPORTED_FILE_TYPES : SUPPORTED_IMG_TYPES

    this.setState({ isLoading: true })
    const files = accepted.filter(item =>
      supportedTypes.some(supportedType => item.type === supportedType)
    )

    if (files.length === 0) {
      this.props.notification('File type is not supported', {
        status: 'error',
        intlKey: 'notifications.file_type_is_not_supported'
      })
      this.setState({ isLoading: false })
      return
    }

    const promises = []

    for (let i = 0; i < files.length; i++) {
      promises.push(this.getBase64(files[i]))
    }

    return Promise.all(promises)
      .then(res => this.setNewFiles(input, res[0]))
      .catch(err => {
        this.setState({ isLoading: false })
        console.log('err', err)
        this.props.notification(
          "File could not be uploaded. It's probably too large.",
          {
            status: 'error',
            intlKey: 'notifications.file_is_too_large'
          }
        )
      })
  }

  getBase64 = file => {
    const reader = new FileReader()

    return new Promise((resolve, reject) => {
      reader.readAsDataURL(file)
      reader.onload = () =>
        this.props
          .onPutBlob({
            blob: this.dataURItoBlob(reader.result),
            fileName: file.name
          })
          .then(res => resolve(get(res, 'body')))
          .catch(err => reject(err))
      reader.onerror = err => reject(err)
    })
  }

  setNewFiles = (input, image) => {
    this.handleDownloadImage(image).then(res => {
      const value = input.value ? [...input.value, res] : [res]

      if (this.props.storeRemoved) {
        value.removed = get(input, 'value.removed')
          ? [...input.value.removed]
          : undefined
      }

      input.onChange(value)
      this.setState({ isLoading: false })
    })
  }

  addBlobToFiles = (image, index) => {
    if (!image.url && typeof image === 'string') {
      image = { url: image }
    }

    this.handleDownloadImage(image).then(res => {
      const files = [...this.props.input.value]

      files[index] = res
      this.props.input.onChange(files)
    })
  }

  removeFile = (index, input) => {
    const value = input.value && [...input.value]
    const removed = value.splice(index, 1)

    if (this.props.storeRemoved) {
      value.removed = get(input, 'value.removed')
        ? [...input.value.removed, ...removed]
        : removed
    }

    input.onChange(value)
  }

  initializeBlob = files => {
    if (!files || !Array.isArray(files)) {
      return
    }

    for (const key in files) {
      if (files.hasOwnProperty(key)) {
        !files[key].blob && this.addBlobToFiles(files[key], key)
      }
    }
  }

  dataURItoBlob = dataURI => {
    // convert base64/URLEncoded data component to raw binary data held in a string
    const byteString =
      dataURI.split(',')[0].indexOf('base64') >= 0
        ? atob(dataURI.split(',')[1])
        : unescape(dataURI.split(',')[1])
    // separate out the mime component
    const mimeString = dataURI
      .split(',')[0]
      .split(':')[1]
      .split(';')[0]
    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    return new Blob([ia], { type: mimeString })
  }

  handleDownloadImage = image => {
    const { url } = image

    if (!__CLIENT__) {
      return
    }
    const xhr = new XMLHttpRequest()

    xhr.open('GET', url, true)
    xhr.setRequestHeader('Authorization', 'Bearer ' + getToken(this.props.user))
    xhr.responseType = 'blob'

    return new Promise((resolve, reject) => {
      /* eslint-disable */
      xhr.onload = function() {
        const reader = new window.FileReader()

        reader.onload = () =>
          resolve({
            ...image,
            blob: 'data:image/png;base64,' + reader.result.split(',')[1]
          })
        reader.onerror = err => reject(err)
        reader.readAsDataURL(this.response)
      }
      /* eslint-enable */
      xhr.onerror = err => reject(err)
      xhr.send()
    })
  }

  handleGetFileName = url => {
    if (!url) {
      return
    }

    const fileName = decodeURIComponent(url).replace(
      /^.*(\\|\/|\:)/, // eslint-disable-line
      ''
    )

    return fileName.split('.')[0]
  }

  render() {
    const { isLoading } = this.state
    const { meta, input, isFile } = this.props

    const isError = meta.touched && meta.error
    const classNames = [styles.control]

    isError && classNames.push('error')

    const defaultMessage = isFile ? 'Add document' : 'Add picture'
    const intlKey = isFile ? 'label.add_document' : 'label.add_picture'
    const icon = isFile ? 'file-other' : 'camera'
    const supportedTypes = isFile ? SUPPORTED_FILE_TYPES : SUPPORTED_IMG_TYPES

    return (
      <div>
        <div className={styles.wrap}>
          {input.value &&
            input.value.map((item, index) => (
              <div key={index} className={styles.previewWrap}>
                <i
                  className={`ico ico--close ${styles.previewClose}`}
                  onClick={() => this.removeFile(index, input)}
                />
                <div
                  className={styles.previewImage}
                  style={{ backgroundImage: `url(${item.blob})` }}
                />
                <span className={styles.filename}>
                  {this.handleGetFileName(item.url)}
                </span>

                <span className={`ico ico--checked ${styles.icoChecked}`} />
              </div>
            ))}
          <Dropzone
            className={classNames.join(' ')}
            accept={supportedTypes.join(',')}
            onDrop={(accepted, rejected) =>
              this.onDrop(accepted, rejected, input)}
            multiple={false}
          >
            {isLoading && (
              <div className={styles.loader}>
                <MaterialLoader />
              </div>
            )}
            {!isLoading && (
              <div className={`i ico ico--${icon} ${styles.camera}`} />
            )}
            {!isLoading && (
              <p className={styles.caption}>
                <FormattedMessage
                  id={intlKey}
                  defaultMessage={defaultMessage}
                />
              </p>
            )}
          </Dropzone>
        </div>

        <FieldError toShow={isError} message={meta.error} />
      </div>
    )
  }
}
